/**
 * Wound Care Overlay Component
 * 
 * AI-powered wound assessment and documentation overlay with:
 * - Phase 1: Image analysis, documentation, note generation
 * - Phase 2: Compliance alerts, care plan prompts, progression tracking
 */

import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { 
  Bandage, History, Plus, Brain, AlertTriangle, TrendingUp, 
  TrendingDown, Bell, ClipboardList, Activity, ChevronRight,
  Loader2, Clock, CheckCircle
} from "lucide-react";
import WoundAssessment from "../WoundAssessment";
import WoundProgressionChart from "../WoundProgressionChart";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export function WoundCareOverlay({ patientId, patientName, onSave, onClose }) {
  const [activeTab, setActiveTab] = useState("new");
  const [woundHistory, setWoundHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [overdueAlerts, setOverdueAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [carePlanPrompts, setCarePlanPrompts] = useState([]);
  const [selectedWoundForProgress, setSelectedWoundForProgress] = useState(null);
  const [woundsSummary, setWoundsSummary] = useState(null);

  // Load overdue wound alerts
  const loadOverdueAlerts = useCallback(async () => {
    setLoadingAlerts(true);
    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(
        `${API}/api/wound-assessment/alerts/overdue/${patientId}`,
        { headers: { "Authorization": `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setOverdueAlerts(data.overdue_wounds || []);
      }
    } catch (error) {
      console.error("Failed to load overdue alerts:", error);
    } finally {
      setLoadingAlerts(false);
    }
  }, [patientId]);

  // Load wounds summary with trends
  const loadWoundsSummary = useCallback(async () => {
    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(
        `${API}/api/wound-assessment/progression/summary/${patientId}`,
        { headers: { "Authorization": `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setWoundsSummary(data);
      }
    } catch (error) {
      console.error("Failed to load wounds summary:", error);
    }
  }, [patientId]);

  // Load overdue alerts on mount
  useEffect(() => {
    if (patientId) {
      loadOverdueAlerts();
      loadWoundsSummary();
    }
  }, [patientId, loadOverdueAlerts, loadWoundsSummary]);

  // Load wound history
  const loadHistory = async () => {
    if (woundHistory.length > 0) return;
    
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(`${API}/api/wound-assessment/assessments/${patientId}`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWoundHistory(data.assessments || []);
      }
    } catch (error) {
      console.error("Failed to load wound history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Check care plan updates after assessment
  const checkCarePlanUpdates = async (woundId) => {
    try {
      const token = localStorage.getItem("hospice_token");
      const response = await fetch(
        `${API}/api/wound-assessment/care-plan-check?patient_id=${patientId}&wound_id=${woundId}`,
        { 
          method: "POST",
          headers: { "Authorization": `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.care_plan_updates_needed) {
          setCarePlanPrompts(data.prompts);
        }
      }
    } catch (error) {
      console.error("Failed to check care plan updates:", error);
    }
  };

  const handleComplete = async (data) => {
    if (onSave) onSave(data);
    
    // Check if care plan updates are needed
    if (data.assessment_id) {
      await checkCarePlanUpdates(data.assessment_id);
    }
    
    // Refresh data
    setWoundHistory([]);
    loadHistory();
    loadOverdueAlerts();
    loadWoundsSummary();
  };

  const dismissCarePlanPrompt = (index) => {
    setCarePlanPrompts(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4 p-4" data-testid="wound-care-overlay">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <Bandage className="w-5 h-5 text-orange-400" />
        <h3 className="font-semibold text-white">AI-Powered Wound Assessment</h3>
        <Badge variant="outline" className="border-violet-500/50 text-violet-300 flex items-center gap-1">
          <Brain className="w-3 h-3" />
          GPT-4o Vision
        </Badge>
        <Badge variant="outline" className="border-slate-600 text-slate-300 ml-auto">
          {patientName}
        </Badge>
      </div>

      {/* Overdue Alerts Banner */}
      {overdueAlerts.length > 0 && (
        <Card className="bg-amber-900/30 border-amber-600/50">
          <CardContent className="p-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-amber-200 font-medium text-sm">
                  {overdueAlerts.length} Wound{overdueAlerts.length > 1 ? "s" : ""} Overdue for Reassessment
                </p>
                <div className="space-y-1 mt-2">
                  {overdueAlerts.map((alert, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="text-amber-300">
                        {alert.location} - {alert.wound_type?.replace(/_/g, " ")}
                      </span>
                      <Badge className={`text-xs ${
                        alert.priority === "critical" ? "bg-red-500/30 text-red-300" :
                        alert.priority === "high" ? "bg-amber-500/30 text-amber-300" :
                        "bg-slate-600 text-slate-300"
                      }`}>
                        {alert.days_overdue} days overdue
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Care Plan Update Prompts */}
      {carePlanPrompts.length > 0 && (
        <Card className="bg-blue-900/30 border-blue-600/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-blue-200">
              <ClipboardList className="w-4 h-4" />
              Care Plan Updates Recommended
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {carePlanPrompts.map((prompt, index) => (
              <div key={index} className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <Badge className={`text-xs ${
                      prompt.urgency === "critical" ? "bg-red-500/30 text-red-300" :
                      prompt.urgency === "high" ? "bg-amber-500/30 text-amber-300" :
                      "bg-blue-500/30 text-blue-300"
                    }`}>
                      {prompt.urgency.toUpperCase()}
                    </Badge>
                    <p className="text-white text-sm mt-1">{prompt.trigger_reason}</p>
                    <p className="text-slate-400 text-xs mt-0.5">
                      Status: {prompt.previous_status} → {prompt.current_status}
                    </p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-slate-400 hover:text-white"
                    onClick={() => dismissCarePlanPrompt(index)}
                  >
                    Dismiss
                  </Button>
                </div>
                <div className="mt-2">
                  <p className="text-xs text-slate-500 mb-1">Recommended Actions:</p>
                  <ul className="space-y-1">
                    {prompt.recommended_updates.map((update, i) => (
                      <li key={i} className="text-xs text-slate-300 flex items-start gap-2">
                        <ChevronRight className="w-3 h-3 mt-0.5 text-blue-400 flex-shrink-0" />
                        {update}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                    Update Care Plan
                  </Button>
                  <Button size="sm" variant="outline">
                    Document Response
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Quick Stats */}
      {woundsSummary && woundsSummary.total_wounds > 0 && (
        <div className="grid grid-cols-4 gap-2">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-white">{woundsSummary.active_wounds}</p>
              <p className="text-xs text-slate-400">Active Wounds</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-400">{woundsSummary.resolved_wounds}</p>
              <p className="text-xs text-slate-400">Resolved</p>
            </CardContent>
          </Card>
          <Card className={`border-slate-700 ${woundsSummary.wounds_worsening > 0 ? "bg-red-900/20" : "bg-slate-800/50"}`}>
            <CardContent className="p-3 text-center">
              <p className={`text-2xl font-bold ${woundsSummary.wounds_worsening > 0 ? "text-red-400" : "text-slate-400"}`}>
                {woundsSummary.wounds_worsening}
              </p>
              <p className="text-xs text-slate-400">Worsening</p>
            </CardContent>
          </Card>
          <Card className={`border-slate-700 ${woundsSummary.wounds_with_infection > 0 ? "bg-amber-900/20" : "bg-slate-800/50"}`}>
            <CardContent className="p-3 text-center">
              <p className={`text-2xl font-bold ${woundsSummary.wounds_with_infection > 0 ? "text-amber-400" : "text-slate-400"}`}>
                {woundsSummary.wounds_with_infection}
              </p>
              <p className="text-xs text-slate-400">Infection</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => {
        setActiveTab(v);
        if (v === "history") loadHistory();
        if (v === "trends") loadWoundsSummary();
      }}>
        <TabsList className="grid w-full grid-cols-3 bg-slate-800">
          <TabsTrigger value="new" className="flex items-center gap-2 data-[state=active]:bg-teal-600">
            <Plus className="w-4 h-4" />
            New Assessment
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2 data-[state=active]:bg-slate-600">
            <History className="w-4 h-4" />
            History
          </TabsTrigger>
          <TabsTrigger value="trends" className="flex items-center gap-2 data-[state=active]:bg-violet-600">
            <Activity className="w-4 h-4" />
            Trends
          </TabsTrigger>
        </TabsList>

        {/* New Assessment Tab */}
        <TabsContent value="new" className="mt-4">
          <WoundAssessment
            patientId={patientId}
            patientName={patientName}
            onComplete={handleComplete}
            onClose={onClose}
          />
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-4">
          {loadingHistory ? (
            <div className="text-center py-8 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading wound history...
            </div>
          ) : woundHistory.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Bandage className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No wound assessments on file for this patient</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setActiveTab("new")}
              >
                Create First Assessment
              </Button>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {woundHistory.map((wound, index) => {
                const isOverdue = overdueAlerts.some(a => a.wound_id === wound.id);
                
                return (
                  <div
                    key={wound.id || index}
                    className={`p-4 rounded-lg border ${
                      isOverdue 
                        ? "bg-amber-900/20 border-amber-600/50" 
                        : "bg-slate-800/50 border-slate-700"
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-white">
                            {wound.location?.anatomical_site || "Unknown Location"}
                          </h4>
                          {isOverdue && (
                            <Badge className="bg-amber-500/30 text-amber-300 text-xs">
                              <Clock className="w-3 h-3 mr-1" />
                              Overdue
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-400">
                          {wound.characteristics?.wound_type?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                          {wound.characteristics?.stage && ` - Stage ${wound.characteristics.stage}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={
                          wound.status === "resolved" ? "default" : 
                          wound.status === "monitoring" ? "secondary" : "outline"
                        }>
                          {wound.status || "Active"}
                        </Badge>
                        <Badge className={`text-xs ${
                          wound.comparison_to_prior === "improved" ? "bg-green-500/20 text-green-300" :
                          wound.comparison_to_prior === "declined" ? "bg-red-500/20 text-red-300" :
                          wound.comparison_to_prior === "stable" ? "bg-blue-500/20 text-blue-300" :
                          "bg-slate-600 text-slate-300"
                        }`}>
                          {wound.comparison_to_prior === "improved" && <TrendingDown className="w-3 h-3 mr-1" />}
                          {wound.comparison_to_prior === "declined" && <TrendingUp className="w-3 h-3 mr-1" />}
                          {wound.comparison_to_prior?.charAt(0).toUpperCase() + wound.comparison_to_prior?.slice(1) || "New"}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm mt-3">
                      <div>
                        <span className="text-slate-500">Size:</span>
                        <span className="ml-2 text-slate-300">
                          {wound.measurements?.length_cm} × {wound.measurements?.width_cm} cm
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500">Drainage:</span>
                        <span className="ml-2 text-slate-300">
                          {wound.characteristics?.drainage_amount || "None"}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500">Pain:</span>
                        <span className="ml-2 text-slate-300">
                          {wound.pain?.pain_score}/10
                        </span>
                      </div>
                    </div>

                    {wound.infection?.signs_present && (
                      <div className="mt-2 p-2 bg-red-900/20 rounded border border-red-600/30">
                        <p className="text-xs text-red-300 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Infection signs present
                          {wound.infection.provider_notified && (
                            <Badge className="ml-2 bg-green-500/20 text-green-300 text-xs">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Provider Notified
                            </Badge>
                          )}
                        </p>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700">
                      <span className="text-xs text-slate-500">
                        {new Date(wound.created_at).toLocaleDateString()} by {wound.created_by_name}
                      </span>
                      <div className="flex gap-2">
                        {wound.compliance_alerts?.length > 0 && (
                          <Badge variant="destructive" className="text-xs">
                            {wound.compliance_alerts.length} Alert{wound.compliance_alerts.length > 1 ? "s" : ""}
                          </Badge>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-xs"
                          onClick={() => {
                            setSelectedWoundForProgress(wound.location?.anatomical_site);
                            setActiveTab("trends");
                          }}
                        >
                          <Activity className="w-3 h-3 mr-1" />
                          View Trend
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="mt-4">
          {woundsSummary && woundsSummary.wounds?.length > 0 ? (
            <div className="space-y-4">
              {/* Wound Selection */}
              {!selectedWoundForProgress ? (
                <div className="space-y-3">
                  <p className="text-sm text-slate-400">Select a wound to view progression:</p>
                  {woundsSummary.wounds.map((wound, index) => (
                    <Card 
                      key={index}
                      className="bg-slate-800/50 border-slate-700 cursor-pointer hover:bg-slate-800 transition-colors"
                      onClick={() => setSelectedWoundForProgress(wound.location)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-white">{wound.location}</h4>
                            <p className="text-sm text-slate-400">
                              {wound.wound_type?.replace(/_/g, " ")} • {wound.current_size}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={`${
                              wound.trend === "improving" ? "bg-green-500/20 text-green-300" :
                              wound.trend === "worsening" ? "bg-red-500/20 text-red-300" :
                              wound.trend === "stable" ? "bg-blue-500/20 text-blue-300" :
                              "bg-slate-600 text-slate-300"
                            }`}>
                              {wound.trend === "improving" && <TrendingDown className="w-3 h-3 mr-1" />}
                              {wound.trend === "worsening" && <TrendingUp className="w-3 h-3 mr-1" />}
                              {wound.trend.charAt(0).toUpperCase() + wound.trend.slice(1)}
                            </Badge>
                            <span className="text-xs text-slate-500">
                              {wound.assessment_count} assessments
                            </span>
                            <ChevronRight className="w-4 h-4 text-slate-500" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="mb-3"
                    onClick={() => setSelectedWoundForProgress(null)}
                  >
                    ← Back to wound list
                  </Button>
                  <WoundProgressionChart
                    patientId={patientId}
                    location={selectedWoundForProgress}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Activity className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No wound progression data available</p>
              <p className="text-sm mt-2">Create wound assessments to track progression over time</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default WoundCareOverlay;
